import React from "react";
import { Tooltip } from "@/components/common/Tooltip";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";

import { SelectCheckbox } from "components/common/SelectCheckbox";
import { TableItem } from "./TableItem";
import { DeleteDialogItem } from "./DeleteDialogItem";
import { TransferTable, fadeOutClass } from "./TransferTypes";

interface TableSectionProps {
  title: string;
  count: number;
  tables: TransferTable[];
  expanded: boolean;
  onToggleExpanded: () => void;
  selectedTables: Set<string>;
  onToggleSelection: (tableId: string, e: React.MouseEvent) => void;
  onSelectAll: () => void;
  draggedTableIds: Set<string>;
  transferringTableId?: string | null;
  transferringBatchIds?: Set<string>;
  transferSuccessTableIds?: Set<string>;
  fadingOutTableIds?: Set<string>;
  severity: { color: string; tooltip: string };
}

export const TableSection: React.FC<TableSectionProps> = ({
  title,
  count,
  tables,
  expanded,
  onToggleExpanded,
  selectedTables,
  onToggleSelection,
  onSelectAll,
  draggedTableIds,
  transferringTableId = null,
  transferringBatchIds = new Set(),
  transferSuccessTableIds = new Set(),
  fadingOutTableIds = new Set(),
  severity,
}) => {
  const allTablesSelected =
    tables.length > 0 && tables.every((t) => selectedTables.has(t.id));

  return (
    <div>
      <DeleteDialogItem
        className="group justify-between cursor-pointer"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-2 w-full">
          <KeyboardArrowDownRoundedIcon
            className={`icon-sm text-fg-muted-regular transition-transform ${expanded ? "" : "-rotate-90"}`}
          />
          <Tooltip title={severity.tooltip} placement="bottom">
            <div className="flex items-center gap-2 flex-1">
              <ReportOutlinedIcon
                className={`icon-sm ${severity.color} transition-colors`}
              />
              <span className="text-fg-medium cursor-default select-none">
                {title} ({count})
              </span>
            </div>
          </Tooltip>
          <div
            className={`flex items-center transition-opacity ${selectedTables.size > 0 ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <SelectCheckbox
              selected={allTablesSelected}
              onToggle={(e) => {
                e.stopPropagation();
                onSelectAll();
              }}
              tooltip={{ checked: "Deselect all", unchecked: "Select all" }}
              size="sm"
            />
          </div>
        </div>
      </DeleteDialogItem>
      {expanded && (
        <>
          {tables.map((table) => (
            <div
              key={table.id}
              className={fadeOutClass(fadingOutTableIds.has(table.id))}
            >
              <TableItem
                table={table}
                isSelected={selectedTables.has(table.id)}
                selectionActive={selectedTables.size > 0}
                onToggleSelection={(e) => onToggleSelection(table.id, e)}
                isDragging={draggedTableIds.has(table.id)}
                isTransferring={
                  transferringTableId === table.id ||
                  transferringBatchIds.has(table.id)
                }
                isTransferSuccess={transferSuccessTableIds.has(table.id)}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};
